import {ConfigManager} from '@pexip/config-manager';
import {configHook} from '@pexip/config-manager/configHook';
import type {MediaDeviceInfoLike} from '@pexip/media-control';
import {
    CURSOR_CAPTURE_CONSTRAINT,
    DISPLAY_CAPTURE_SURFACE_TYPE,
    INCLUDE_EXCLUDE_CONSTRAINT,
} from '@pexip/media-control';
import {AUDIO_CONTENT_HINTS, VIDEO_CONTENT_HINTS} from '@pexip/media';
import {
    useAssertedContext,
    currentBrowserName,
    isMobileDevice,
} from '@pexip/media-components';

import {
    ALLOW_CHANGE_OF_MEDIA_DIRECTION,
    BACKGROUND_BLUR_AMOUNT,
    BG_IMAGE_URL,
    CALL_TYPE,
    EDGE_BLUR_AMOUNT,
    FOREGROUND_THRESHOLD,
    INITIAL_BANDWIDTH,
    MASK_COMBINE_RATIO,
    PREFER_PRES_IN_MIX,
    RENDER_EFFECTS,
} from './constants';
import {
    BrowserCloseConfirmation,
    StepByStepCompletion,
    type AppConfig,
    type DefaultUserConfig,
    type InternalConifg,
} from './types';
import {ConfigContext} from './contexts/config.context';
import {isChromiumWindows} from './utils/userAgentData';

export const shouldEnableVideoProcessing = () =>
    !isMobileDevice() &&
    // Since Safari v18, it adds the MediaStreamTrackProcessor API
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrackProcessor#browser_compatibility
    // Where it requires to use these new APIs to avoid background throttling for video processing
    // Reference https://github.com/pexip/aquila/issues/2742
    // Disable video processing until the fixes landed
    !currentBrowserName?.startsWith('Safari') &&
    (currentBrowserName === 'Chrome' ||
        currentBrowserName === 'Firefox' ||
        currentBrowserName === 'Edge Chromium' ||
        currentBrowserName === 'Safari Mac') &&
    (() => {
        const WEBGL_ATTRIBUTES = {
            alpha: false,
            antialias: false,
            premultipliedAlpha: false,
            preserveDrawingBuffer: false,
            depth: false,
            stencil: false,
            failIfMajorPerformanceCaveat: true,
        };
        try {
            const canvas = document.createElement('canvas');
            return !!(
                window.WebGLRenderingContext &&
                (canvas.getContext('webgl', WEBGL_ATTRIBUTES) ||
                    canvas.getContext('experimental-webgl', WEBGL_ATTRIBUTES))
            );
        } catch (_e) {
            return false;
        }
    })();

export const shouldEnableAudioProcessing = () => !isMobileDevice();
export const shouldEnableAudioTuneables = () =>
    !currentBrowserName?.includes('Safari');

export const internalConfig: InternalConifg = {
    asd: false,
    // FIXME: We should not use empty object as a default value for audioInput, audioOutput and videoInput
    audioInput: {} as MediaDeviceInfoLike,
    audioOutput: {} as MediaDeviceInfoLike,
    lastFallBackMsg: '',
    showLayoutChangeConfirmationModal: true,
    showSelfviewHiddenTooltip: true,
    vad: false,
    videoInput: {} as MediaDeviceInfoLike,
    stepByStepCompletion: StepByStepCompletion.Uncompleted,
};

export const defaultUserConfig: DefaultUserConfig = {
    allowChangeOfMediaDirection: ALLOW_CHANGE_OF_MEDIA_DIRECTION,
    audioContentHint: AUDIO_CONTENT_HINTS.Speech,
    autoGainControl: shouldEnableAudioTuneables(),
    backgroundBlurAmount: BACKGROUND_BLUR_AMOUNT,
    bandwidth: INITIAL_BANDWIDTH,
    bgImageUrl: BG_IMAGE_URL,
    callType: CALL_TYPE,
    curosrCapture: CURSOR_CAPTURE_CONSTRAINT.Always,
    denoise: false,
    displayName: '',
    isGuest: false,
    displaySurface: DISPLAY_CAPTURE_SURFACE_TYPE.Window,
    echoCancellation: true,
    edgeBlurAmount: EDGE_BLUR_AMOUNT,
    fecc: false,
    maskCombineRatio: MASK_COMBINE_RATIO,
    foregroundThreshold: FOREGROUND_THRESHOLD,
    isAudioInputMuted: false,
    isUserFacing: true,
    isVideoInputMuted: false,
    noiseSuppression: shouldEnableAudioTuneables(),
    presoContentHint: VIDEO_CONTENT_HINTS.Detail,
    segmentationEffects: RENDER_EFFECTS,
    showSelfView: true,
    surfaceSwitching: INCLUDE_EXCLUDE_CONSTRAINT.Include,
    userBgImageAssets: [] as string[],
    videoContentHint: VIDEO_CONTENT_HINTS.NoHint,
    videoProcessingAPI: 'stream',
    videoProcessingDelegate: 'GPU',
    // WebGPU API might causes alternating the muted events for Windows users
    gpuAPI: isChromiumWindows() ? 'webgl' : 'webgpu',
    captureAudio: true,
    join: false,
    preferPresInMix: PREFER_PRES_IN_MIX,
    mirrorSelfview: true,
    enableBrowserCloseConfirmationByDefault: false,
    browserCloseConfirmation: BrowserCloseConfirmation.Unset,
    enforceAudioMute: false,
    enforceVideoMute: false,
    // If the user is on a touch device we want to always show UI controls by default, not auto hide them,
    // because mobile screens readers need to be able to access the controls
    alwaysDisplayUIInterfaces:
        'matchMedia' in window
            ? window.matchMedia('(pointer: coarse)').matches
            : true,
    showParticipantAuthenticatedState: false,
    disableKeyboardShortcuts: false,
    stopCameraAsMute: currentBrowserName !== 'Firefox',
};

export const config = new ConfigManager<AppConfig>(
    {
        ...internalConfig,
        ...defaultUserConfig,
    },
    'infinity-connect',
);

export const useConfigContext = () => useAssertedContext(ConfigContext);

export const useConfig = <T extends keyof AppConfig>(key: T) =>
    configHook(useConfigContext())(key);
